import { FlexibleModuleEnum } from 'generated/graphql';
import type {
    PageBuilderModuleType,
    ImageColumnProps,
} from '@modules/page-builder/types';
import type { Image } from '@modules/page-builder/type/message-passing';

/**
 * Interface for extracted image data from project modules
 */
interface ProjectImageData {
    /** Unique identifier for the image */
    id: string;
    /** Image URL for display */
    imageUrl: string;
    /** Video URL if this is a video */
    videoUrl?: string;
    /** Image caption if available */
    caption?: string;
    /** Library image ID if available */
    libraryImageId?: number;
    /** Library image object if available */
    libraryImage?: Image;
    /** Module type that contains this image */
    moduleType: string;
    /** Module ID that contains this image */
    moduleId: string;
    /** Column ID within the module */
    columnId: string;
    /** Product ID if this image is linked to a product */
    productId?: number;
    /** Image dimensions */
    dimension?: {
        width: number;
        height: number;
    };
    /** Image aspect ratio */
    aspectRatio?: number;
    /** Whether the image is portrait orientation */
    isPortrait?: boolean;
}

/**
 * Extracts all images from project page modules for use in a carousel
 *
 * @param modules - Array of page builder modules from the project
 * @returns Array of image data objects
 */
export const extractProjectImages = (
    modules: PageBuilderModuleType[]
): ProjectImageData[] => {
    const images: ProjectImageData[] = [];

    // Define module types that contain images (excluding product-related modules)
    const imageModuleTypes = [
        FlexibleModuleEnum.SingleImageModule,
        FlexibleModuleEnum.DuoImageModule,
        FlexibleModuleEnum.QuadImageModule,
        FlexibleModuleEnum.OctaImageModule,
        FlexibleModuleEnum.ImageParagraphModule,
        FlexibleModuleEnum.SingleVideoModule,
        FlexibleModuleEnum.VideoParagraphModule,
    ];

    // Skip ProductFeature modules as we're not displaying products in the banner
    const excludedModuleTypes = [
        'ProductFeature', //
    ];

    modules.forEach((module) => {
        if (
            !imageModuleTypes.includes(
                module.presetType as FlexibleModuleEnum
            ) ||
            excludedModuleTypes.includes(module.presetType)
        ) {
            return;
        }

        // Since the ecommerceEnabled flag is set to true when the GalleryModule is
        // used to display products (single/double row prod collection in Page Builder)
        // we will skip these modules as we're not displaying products in the banner
        if (
            module.ecommerceEnabled &&
            (module.presetType === FlexibleModuleEnum.QuadImageModule ||
                module.presetType === FlexibleModuleEnum.OctaImageModule)
        ) {
            return;
        }

        // Process each column in the module
        module.columns.forEach((column) => {
            // Check if this column has image data
            if (hasImageData(column)) {
                const imageData: ProjectImageData = {
                    id: `${module.id}-${column.id}`,
                    imageUrl: column.imageUrl!,
                    videoUrl: column.video?.videoURL,
                    caption: column.caption,
                    libraryImageId: column.libraryImage?.ID,
                    libraryImage: column.libraryImage,
                    moduleType: module.presetType,
                    moduleId: module.id,
                    columnId: column.id,
                    productId: column.productId,
                    dimension: column.dimension,
                    aspectRatio: column.imageAspectRatio,
                    isPortrait: column.imageIsPortrait,
                };

                images.push(imageData);
            }

            // For video modules, also include video thumbnails
            if (
                module.presetType === FlexibleModuleEnum.SingleVideoModule ||
                module.presetType === FlexibleModuleEnum.VideoParagraphModule
            ) {
                if (column.video?.coverImageThumbnail) {
                    const videoImageData: ProjectImageData = {
                        id: `${module.id}-${column.id}-video-thumb`,
                        imageUrl: column.video.coverImageThumbnail,
                        videoUrl: column.video.videoURL,
                        caption: column.video.title || 'Video thumbnail',
                        moduleType: module.presetType,
                        moduleId: module.id,
                        columnId: column.id,
                        productId: undefined,
                    };

                    images.push(videoImageData);
                }
            }
        });
    });

    return images;
};

/**
 * Helper function to check if a column contains image data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasImageData = (column: any): column is ImageColumnProps => {
    return (
        column &&
        typeof column === 'object' &&
        'imageUrl' in column &&
        column.imageUrl &&
        typeof column.imageUrl === 'string'
    );
};

/**
 * Filters images by module type
 *
 * @param images - Array of project images
 * @param moduleTypes - Array of module types to include
 * @returns Filtered array of images
 */
export const filterImagesByModuleType = (
    images: ProjectImageData[],
    moduleTypes: string[]
): ProjectImageData[] => {
    return images.filter((image) => moduleTypes.includes(image.moduleType));
};

/**
 * Sorts images by their module order and column order
 *
 * @param images - Array of project images
 * @returns Sorted array of images
 */
export const sortImagesByOrder = (
    images: ProjectImageData[]
): ProjectImageData[] => {
    return [...images].sort((a, b) => {
        // Extract module order from module ID (assuming format like "module-123")
        const aModuleOrder = parseInt(a.moduleId.split('-').pop() || '0', 10);
        const bModuleOrder = parseInt(b.moduleId.split('-').pop() || '0', 10);

        if (aModuleOrder !== bModuleOrder) {
            return aModuleOrder - bModuleOrder;
        }

        // If same module, sort by column ID
        const aColumnOrder = parseInt(a.columnId.split('-').pop() || '0', 10);
        const bColumnOrder = parseInt(b.columnId.split('-').pop() || '0', 10);

        return aColumnOrder - bColumnOrder;
    });
};
